import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useExpenditures, useCreateExpenditure, useUpdateExpenditure, useDeleteExpenditure, useExpenditureSummary } from '@/hooks/usePSI';
import { IExpenditure, IExpenditureCreateRequest, EXPENDITURE_CATEGORIES, EXPENDITURE_MAIN_CATEGORIES, PAYMENT_METHODS, ExpenditurePeriod } from '@/types/psi';
import SearchBar from '@/components/SearchBar';
import Pagination from '@/components/Pagination';
import LoadingSpinner from '@/components/LoadingSpinner';
import { MobileCard } from '@/components/ui';

const ExpenditureList: React.FC = () => {
  const { t } = useTranslation('psi');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'summary'>('list');
  const [summaryPeriod, setSummaryPeriod] = useState<ExpenditurePeriod>('monthly');
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  const [newExpenditure, setNewExpenditure] = useState<Partial<IExpenditureCreateRequest>>({
    date: new Date().toISOString().split('T')[0],
    status: 'pending',
    currency: 'TWD',
    is_recurring: false,
    recurring_frequency: 'none',
  });
  const [selectedCategory, setSelectedCategory] = useState('');

  const { data, isLoading, refetch } = useExpenditures({ page, pageSize, q: searchQuery });
  const { data: summaryData, isLoading: isSummaryLoading } = useExpenditureSummary(summaryYear, summaryPeriod);
  const createMutation = useCreateExpenditure();
  const updateMutation = useUpdateExpenditure();
  const deleteMutation = useDeleteExpenditure();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setNewExpenditure({ ...newExpenditure, category, sub_category: '' });
  };

  const handleAddNew = async () => {
    if (!newExpenditure.category || !newExpenditure.description || !newExpenditure.amount) {
      alert(t('expenditure.validation.required'));
      return;
    }

    try {
      await createMutation.mutateAsync(newExpenditure as IExpenditureCreateRequest);
      setIsAddingNew(false);
      setNewExpenditure({
        date: new Date().toISOString().split('T')[0],
        status: 'pending',
        currency: 'TWD',
        is_recurring: false,
        recurring_frequency: 'none',
      });
      setSelectedCategory('');
    } catch (error) {
      logger.error('Failed to create expenditure:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm(t('expenditure.confirmDelete'))) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        logger.error('Failed to delete expenditure:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle & Controls */}
      <div className="flex flex-col gap-3 sm:gap-4">
        {/* View mode toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px] ${
              viewMode === 'list'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {t('expenditure.listView')}
          </button>
          <button
            onClick={() => setViewMode('summary')}
            className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors min-h-[44px] ${
              viewMode === 'summary'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {t('expenditure.summaryView')}
          </button>
        </div>

        {viewMode === 'list' && (
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 sm:items-center sm:justify-between">
            <SearchBar
              value={searchQuery}
              onChange={handleSearch}
              placeholder={t('expenditure.searchPlaceholder')}
            />
            <div className="flex gap-2">
              <button
                onClick={() => refetch()}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px]"
              >
                {t('common.refresh')}
              </button>
              <button
                onClick={() => setIsAddingNew(true)}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 min-h-[44px]"
              >
                <span className="sm:hidden">+ Add</span>
                <span className="hidden sm:inline">{t('expenditure.addExpenditure')}</span>
              </button>
            </div>
          </div>
        )}

        {viewMode === 'summary' && (
          <div className="flex gap-2 items-center">
            <select
              value={summaryYear}
              onChange={(e) => setSummaryYear(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
            <select
              value={summaryPeriod}
              onChange={(e) => setSummaryPeriod(e.target.value as ExpenditurePeriod)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="monthly">{t('expenditure.monthly')}</option>
              <option value="quarterly">{t('expenditure.quarterly')}</option>
              <option value="yearly">{t('expenditure.yearly')}</option>
            </select>
          </div>
        )}
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {data?.expenditures?.map((exp) => (
              <MobileCard
                key={exp.expenditure_id}
                title={exp.description}
                subtitle={`${exp.category} / ${exp.sub_category}`}
                status={
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(exp.status)}`}>
                    {t(`expenditure.status.${exp.status}`)}
                  </span>
                }
                fields={[
                  { label: t('expenditure.columns.date'), value: exp.date },
                  { label: t('expenditure.columns.vendor'), value: exp.vendor || '-' },
                  { label: t('expenditure.columns.amount'), value: formatCurrency(exp.amount, exp.currency), highlight: true },
                ]}
                actions={[
                  { label: t('common.delete'), onClick: () => handleDelete(exp.expenditure_id), variant: 'danger' as const },
                ]}
              />
            ))}
            {(!data?.expenditures || data.expenditures.length === 0) && !isAddingNew && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('expenditure.noData')}</div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.category')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.subCategory')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.description')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.amount')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.vendor')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('expenditure.columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {isAddingNew && (
                  <tr className="bg-green-50 dark:bg-green-900/20">
                    <td className="px-4 py-2">
                      <input
                        type="date"
                        value={newExpenditure.date || ''}
                        onChange={(e) => setNewExpenditure({ ...newExpenditure, date: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="">{t('expenditure.selectCategory')}</option>
                        {EXPENDITURE_MAIN_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={newExpenditure.sub_category || ''}
                        onChange={(e) => setNewExpenditure({ ...newExpenditure, sub_category: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                        disabled={!selectedCategory}
                      >
                        <option value="">{t('expenditure.selectSubCategory')}</option>
                        {selectedCategory && EXPENDITURE_CATEGORIES[selectedCategory]?.map((sub) => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        placeholder={t('expenditure.columns.description')}
                        value={newExpenditure.description || ''}
                        onChange={(e) => setNewExpenditure({ ...newExpenditure, description: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        placeholder={t('expenditure.columns.amount')}
                        value={newExpenditure.amount || ''}
                        onChange={(e) => setNewExpenditure({ ...newExpenditure, amount: parseFloat(e.target.value) || 0 })}
                        className="w-24 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        placeholder={t('expenditure.columns.vendor')}
                        value={newExpenditure.vendor || ''}
                        onChange={(e) => setNewExpenditure({ ...newExpenditure, vendor: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={newExpenditure.status || 'pending'}
                        onChange={(e) => setNewExpenditure({ ...newExpenditure, status: e.target.value as any })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="pending">{t('expenditure.status.pending')}</option>
                        <option value="paid">{t('expenditure.status.paid')}</option>
                        <option value="cancelled">{t('expenditure.status.cancelled')}</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button
                          onClick={handleAddNew}
                          disabled={createMutation.isLoading}
                          className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {t('common.save')}
                        </button>
                        <button
                          onClick={() => {
                            setIsAddingNew(false);
                            setSelectedCategory('');
                            setNewExpenditure({
                              date: new Date().toISOString().split('T')[0],
                              status: 'pending',
                              currency: 'TWD',
                            });
                          }}
                          className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {data?.expenditures?.map((exp) => (
                  <tr key={exp.expenditure_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{exp.date}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{exp.category}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{exp.sub_category}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{exp.description}</td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(exp.amount, exp.currency)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{exp.vendor}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(exp.status)}`}>
                        {t(`expenditure.status.${exp.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleDelete(exp.expenditure_id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!data?.expenditures || data.expenditures.length === 0) && !isAddingNew && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      {t('expenditure.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data && data.total_pages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={data.total_pages}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Summary View */}
      {viewMode === 'summary' && (
        <div className="space-y-6">
          {isSummaryLoading ? (
            <LoadingSpinner />
          ) : summaryData ? (
            <>
              {/* Total Summary Card */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
                <h3 className="text-lg font-medium mb-2">{t('expenditure.totalExpenditure')}</h3>
                <p className="text-3xl font-bold">{formatCurrency(summaryData.grand_total || 0)}</p>
                <p className="text-sm opacity-80 mt-1">
                  {summaryYear} - {t(`expenditure.${summaryPeriod}`)}
                </p>
              </div>

              {/* Category Breakdown */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('expenditure.categoryBreakdown')}
                </h3>
                <div className="space-y-3">
                  {summaryData.category_totals && Object.entries(summaryData.category_totals).map(([category, amount]) => {
                    const percentage = summaryData.grand_total > 0
                      ? ((amount as number) / summaryData.grand_total * 100).toFixed(1)
                      : '0';
                    return (
                      <div key={category} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-gray-600 dark:text-gray-400">{category}</span>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {formatCurrency(amount as number)} ({percentage}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Period Summaries */}
              {summaryData.summaries && summaryData.summaries.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t('expenditure.periodSummary')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {summaryData.summaries.map((summary) => (
                      <div
                        key={summary.period}
                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-500 dark:text-gray-400">{summary.period}</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatCurrency(summary.total_amount)}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {summary.transaction_count} {t('expenditure.transactions')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t('expenditure.noSummaryData')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenditureList;
